import { useState, useEffect } from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter
} from '../ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
    ArrowRight, StickyNote, Plus, Send, Unlock, ShieldAlert,
    Clock, Phone, Mail, MessageSquare, Building2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    qualified: 'bg-purple-100 text-purple-800',
    meeting_scheduled: 'bg-pink-100 text-pink-800',
    negotiation: 'bg-orange-100 text-orange-800',
    closed_won: 'bg-green-100 text-green-800',
    closed_lost: 'bg-gray-100 text-gray-800'
};

const priorityColors = {
    low: 'bg-slate-100 text-slate-800',
    medium: 'bg-sky-100 text-sky-800',
    high: 'bg-red-100 text-red-800'
};

export default function LeadDetailDrawer({ lead, open, onClose, onUpdate }) {
    const { getAuthHeader, agency } = useAuth();
    const [activeTab, setActiveTab] = useState('details');
    const [noteContent, setNoteContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [team, setTeam] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);

    // Reset state and fetch team when lead changes
    useEffect(() => {
        setNoteContent('');
        setActiveTab('details');
        fetchTeam();
    }, [lead?.id]);

    const fetchTeam = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/team`, {
                headers: getAuthHeader()
            });
            setTeam(response.data);
        } catch (err) {
            console.error('Failed to fetch team:', err);
        }
    };

    if (!lead) return null;

    const handleStatusChange = async (newStatus) => {
        setStatusLoading(true);
        try {
            await axios.patch(
                `${API_URL}/api/enquiries/${lead.id}/status`,
                { status: newStatus },
                { headers: getAuthHeader() }
            );
            toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
            onUpdate();
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setStatusLoading(false);
        }
    };

    const handlePriorityChange = async (newPriority) => {
        try {
            await axios.patch(
                `${API_URL}/api/enquiries/${lead.id}/status`,
                { priority: newPriority },
                { headers: getAuthHeader() }
            );
            toast.success('Priority updated');
            onUpdate();
        } catch (error) {
            toast.error('Failed to update priority');
        }
    };

    const handleAddNote = async () => {
        if (!noteContent.trim()) return;
        setLoading(true);
        try {
            await axios.post(
                `${API_URL}/api/enquiries/${lead.id}/notes`,
                { content: noteContent, created_at: new Date().toISOString() },
                { headers: getAuthHeader() }
            );
            toast.success('Note added');
            setNoteContent('');
            onUpdate();
        } catch (error) {
            toast.error('Failed to add note');
        } finally {
            setLoading(false);
        }
    };

    const handleUnlockLead = async () => {
        try {
            const response = await axios.post(`${API_URL}/api/enquiries/${lead.id}/unlock`, {}, {
                headers: getAuthHeader()
            });
            toast.success(response.data.message);
            onUpdate();
        } catch (error) {
            toast.error('Failed to unlock lead');
        }
    };

    const handleAssignAgent = async (agentId) => {
        setAssignLoading(true);
        try {
            await axios.post(`${API_URL}/api/enquiries/${lead.id}/assign?agent_id=${agentId}`, {}, {
                headers: getAuthHeader()
            });
            toast.success('Lead assigned successfully');
            onUpdate();
        } catch (error) {
            toast.error('Failed to assign lead');
        } finally {
            setAssignLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <Sheet open={open} onOpenChange={onClose}>
            <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col h-full bg-white">

                {/* Header */}
                <div className="p-6 border-b border-navy-100 bg-navy-50/50">
                    <SheetHeader className="mb-4">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <Avatar className="h-12 w-12 bg-navy-100 border border-navy-200">
                                    <AvatarFallback className="text-navy-700 font-bold bg-white">
                                        {lead.name.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <SheetTitle className="text-xl font-heading text-navy-900">{lead.name}</SheetTitle>
                                    <SheetDescription className="flex items-center gap-2 mt-1">
                                        <span className="flex items-center gap-1 text-navy-600">
                                            <Clock className="w-3 h-3" /> {formatDate(lead.created_at)}
                                        </span>
                                        <Badge variant="outline" className="text-xs font-normal border-navy-200 bg-white">
                                            {lead.source || 'Website'}
                                        </Badge>
                                    </SheetDescription>
                                </div>
                            </div>
                            <Badge className={`${priorityColors[lead.priority] || 'bg-gray-100'} capitalize`}>
                                {lead.priority || 'Medium'}
                            </Badge>
                        </div>
                    </SheetHeader>

                    {/* Quick Actions / Status Bar */}
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-navy-100 shadow-sm">
                        <div className="flex-1">
                            <p className="text-xs font-medium text-navy-500 mb-1">Current Status</p>
                            <Select value={lead.status} onValueChange={handleStatusChange} disabled={statusLoading}>
                                <SelectTrigger className="h-8 text-sm bg-navy-50 border-navy-200">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">New Lead</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="qualified">Qualified</SelectItem>
                                    <SelectItem value="meeting_scheduled">Meeting Scheduled</SelectItem>
                                    <SelectItem value="negotiation">Negotiation</SelectItem>
                                    <SelectItem value="closed_won">Closed (Won)</SelectItem>
                                    <SelectItem value="closed_lost">Closed (Lost)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <p className="text-xs font-medium text-navy-500 mb-1">Assign To</p>
                            <Select
                                value={lead.assigned_agent_id || "unassigned"}
                                onValueChange={handleAssignAgent}
                                disabled={assignLoading}
                            >
                                <SelectTrigger className="h-8 text-sm bg-navy-50 border-navy-200">
                                    <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {team.map(member => (
                                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-2">
                            <Button size="icon" variant="outline" title="Call" onClick={() => window.open(`tel:${lead.phone}`)} disabled={agency?.subscription === 'free' && !lead.is_unlocked}>
                                <Phone className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button size="icon" variant="outline" title="WhatsApp" onClick={() => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank')} disabled={agency?.subscription === 'free' && !lead.is_unlocked}>
                                <MessageSquare className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button size="icon" variant="outline" title="Email" onClick={() => window.open(`mailto:${lead.email}`)} disabled={agency?.subscription === 'free' && !lead.is_unlocked}>
                                <Mail className="w-4 h-4 text-blue-600" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                        <div className="px-6 pt-2">
                            <TabsList className="w-full grid grid-cols-3 bg-navy-100/50">
                                <TabsTrigger value="details">Details</TabsTrigger>
                                <TabsTrigger value="notes">Notes ({lead.notes?.length || 0})</TabsTrigger>
                                <TabsTrigger value="history">History</TabsTrigger>
                            </TabsList>
                        </div>

                        <ScrollArea className="flex-1 px-6 py-4">

                            <TabsContent value="details" className="space-y-6 mt-0">
                                {/* Contact Info */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Contact Information</h4>
                                    <div className="grid gap-3">
                                        <div className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                                            <Phone className="w-4 h-4 text-navy-400" />
                                            <div className="flex-1">
                                                <p className="text-xs text-navy-500">Phone</p>
                                                <p className="font-medium text-navy-900">{lead.phone}</p>
                                            </div>
                                            {agency?.subscription === 'free' && !lead.is_unlocked && (
                                                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-sky-600 hover:bg-sky-100" onClick={handleUnlockLead}>
                                                    <Unlock className="w-3 h-3 mr-1" /> Unlock
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                                            <Mail className="w-4 h-4 text-navy-400" />
                                            <div className="flex-1">
                                                <p className="text-xs text-navy-500">Email</p>
                                                <p className="font-medium text-navy-900">{lead.email}</p>
                                            </div>
                                            {agency?.subscription === 'free' && !lead.is_unlocked && (
                                                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-sky-600 hover:bg-sky-100" onClick={handleUnlockLead}>
                                                    <Unlock className="w-3 h-3 mr-1" /> Unlock
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {agency?.subscription === 'free' && !lead.is_unlocked && (
                                        <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg flex gap-2">
                                            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
                                            <p className="text-xs text-amber-800">
                                                Contact details are masked for Free plan. Unlock this lead to see full details or upgrade to a premium plan.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <Separator />

                                {/* Property Interest */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Property Interest</h4>
                                    {lead.property_title ? (
                                        <div className="p-4 border border-navy-100 rounded-xl bg-white shadow-sm">
                                            <div className="flex items-center gap-2 text-navy-900 font-medium mb-1">
                                                <Building2 className="w-4 h-4 text-indigo-500" />
                                                {lead.property_title}
                                            </div>
                                            <p className="text-sm text-navy-500 pl-6 mb-3">ID: {lead.property_id}</p>
                                            <Button variant="secondary" size="sm" className="w-full text-xs" onClick={() => window.open(`/agency/properties/${lead.property_id}`, '_blank')}>
                                                <ArrowRight className="w-3 h-3 mr-1" /> View Property
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-navy-50 rounded-lg border border-navy-100 text-center text-navy-500 text-sm italic">
                                            No specific property linked
                                        </div>
                                    )}
                                </div>

                                <Separator />

                                {/* Initial Message */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Initial Message</h4>
                                    <div className="p-4 bg-navy-50 rounded-lg border border-navy-100 text-navy-700 text-sm whitespace-pre-wrap">
                                        {lead.message || 'No message provided.'}
                                    </div>
                                </div>

                                {/* Priority Selection */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Lead Priority</h4>
                                    <div className="flex gap-2">
                                        {['low', 'medium', 'high'].map(p => (
                                            <Button
                                                key={p}
                                                variant={lead.priority === p ? 'default' : 'outline'}
                                                size="sm"
                                                className={`capitalize ${lead.priority === p ? priorityColors[p] : ''}`}
                                                onClick={() => handlePriorityChange(p)}
                                            >
                                                {p}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                            </TabsContent>

                            <TabsContent value="notes" className="space-y-4 mt-0">
                                <div className="space-y-4 mb-4">
                                    {lead.notes && lead.notes.length > 0 ? (
                                        lead.notes.map((note, i) => (
                                            <div key={i} className="flex gap-3">
                                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-indigo-600">
                                                    <StickyNote className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1 bg-white p-3 rounded-lg border border-navy-100 shadow-sm">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className="text-xs font-medium text-navy-900">{note.created_by || 'System'}</span>
                                                        <span className="text-xs text-navy-400">{formatDate(note.created_at)}</span>
                                                    </div>
                                                    <p className="text-sm text-navy-700 whitespace-pre-wrap">{note.content}</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-navy-400 italic">No notes added yet.</div>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="history" className="mt-0">
                                <div className="space-y-6 relative pl-4 border-l-2 border-navy-100 ml-2 py-2">
                                    <div className="relative">
                                        <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-sky-500 ring-4 ring-white" />
                                        <p className="text-sm font-medium text-navy-900">Lead Created</p>
                                        <p className="text-xs text-navy-500">{formatDate(lead.created_at)}</p>
                                    </div>
                                    {/* Placeholder for future activity log */}
                                </div>
                            </TabsContent>

                        </ScrollArea>
                    </Tabs>

                    {/* Add Note Footer (Only visible on Notes tab) */}
                    {activeTab === 'notes' && (
                        <div className="p-4 border-t border-navy-100 bg-white">
                            <div className="flex gap-2">
                                <Textarea
                                    value={noteContent}
                                    onChange={e => setNoteContent(e.target.value)}
                                    placeholder="Add a note..."
                                    className="min-h-[80px] text-sm resize-none"
                                />
                                <Button
                                    className="h-auto bg-navy-900 hover:bg-navy-800"
                                    disabled={loading || !noteContent.trim()}
                                    onClick={handleAddNote}
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

            </SheetContent>
        </Sheet>
    );
}
